import type { IconKind } from '../components/Icon/Icon';

export interface ObDashboardOverviewPeriod {
  total_appointments: number;
  average_visit: number;
  money_collected: number;
  booked_appointments: number;
  upsell_count: number;
  conversion_rate: number;
  insurance_checked: number;
  ai_verified_appointments?: number;
  ai_verified?: number;
  ai_verified_calls?: number;
  call_verified_appointments?: number;
  call_verified?: number;
}

export interface ObDashboardOverviewChange {
  value: number;
  percentage: number;
}

export interface ObDashboardOverviewResponse {
  current_period: ObDashboardOverviewPeriod;
  previous_period: ObDashboardOverviewPeriod;
  changes: {
    total_appointments: ObDashboardOverviewChange;
    average_visit: ObDashboardOverviewChange;
    money_collected: ObDashboardOverviewChange;
    booked_appointments: ObDashboardOverviewChange;
    upsell_count: ObDashboardOverviewChange;
    conversion_rate: ObDashboardOverviewChange;
    insurance_checked: ObDashboardOverviewChange;
  };
  period_info: {
    current_start: string;
    current_end: string;
    previous_start: string;
    previous_end: string;
  };
}

export type ObMetricChangeType = 'increase' | 'decrease' | 'neutral';

export interface ObMetricCardData {
  title: string;
  value: string | number;
  change?: {
    value: string;
    type: ObMetricChangeType;
  } | undefined;
  icon: IconKind;
}

export interface ObAppointmentRow {
  id: number;
  service_name: string;
  provider_name: string;
  verification_status: string;
  created_at: string;
  patient_name: string;
  patient_phone: string;
  patient_email: string;
  appointment_date: string;
  appointment_time: string;
  is_booked: boolean;
  is_payment_done: boolean;
  is_sms_verified: boolean;
  is_call_verified: boolean;
  pre_payment_amount: string;
  call_transcription: string;
  call_audio: string | null;
  existing_patient: number | null;
}

function getChangeType(percentage: number): ObMetricChangeType {
  if (percentage > 0) return 'increase';
  if (percentage < 0) return 'decrease';
  return 'neutral';
}

export function adaptDashboardMetrics(
  apiData: ObDashboardOverviewResponse | null | undefined,
): ObMetricCardData[] {
  if (!apiData?.current_period || !apiData.previous_period || !apiData.changes) {
    return [];
  }

  const { previous_period: prev, changes } = apiData;

  return [
    {
      title: 'Total Visits',
      value: (prev.average_visit || 0).toLocaleString(),
      change: {
        value: `${Math.abs(changes.average_visit?.percentage || 0).toFixed(1)}%`,
        type: getChangeType(changes.average_visit?.percentage || 0),
      },
      icon: 'calendar',
    },
    {
      title: 'Booked Appointments',
      value: (prev.booked_appointments || 0).toLocaleString(),
      change: {
        value: `${Math.abs(changes.booked_appointments?.percentage || 0).toFixed(1)}%`,
        type: getChangeType(changes.booked_appointments?.percentage || 0),
      },
      icon: 'patients',
    },
    {
      title: 'Money Collected',
      value: `$${(prev.money_collected || 0).toLocaleString()}`,
      change: {
        value: `${Math.abs(changes.money_collected?.percentage || 0).toFixed(1)}%`,
        type: getChangeType(changes.money_collected?.percentage || 0),
      },
      icon: 'dollar',
    },
    {
      title: 'Conversion Rate',
      value: `${(prev.conversion_rate || 0).toFixed(1)}%`,
      change: {
        value: `${Math.abs(changes.conversion_rate?.percentage || 0).toFixed(1)}%`,
        type: getChangeType(changes.conversion_rate?.percentage || 0),
      },
      icon: 'trendUp',
    },
    {
      title: 'Upsell Count',
      value: (prev.upsell_count || 0).toString(),
      change: {
        value: `${Math.abs(changes.upsell_count?.percentage || 0).toFixed(1)}%`,
        type: getChangeType(changes.upsell_count?.percentage || 0),
      },
      icon: 'activity',
    },
    {
      title: 'Insurance Checked',
      value: (prev.insurance_checked || 0).toString(),
      change: {
        value: `${Math.abs(changes.insurance_checked?.percentage || 0).toFixed(1)}%`,
        type: getChangeType(changes.insurance_checked?.percentage || 0),
      },
      icon: 'shield-check',
    },
  ];
}

export function resolveAiVerifiedCount(period: ObDashboardOverviewPeriod): number {
  return (
    period.ai_verified_appointments ??
    period.ai_verified ??
    period.ai_verified_calls ??
    period.call_verified_appointments ??
    period.call_verified ??
    0
  );
}

export function formatObDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

export function formatObTime(time: string, fallbackIso?: string): string {
  const source = time ? `2000-01-01T${time}` : fallbackIso;
  if (!source) return '';
  const d = new Date(source);
  if (Number.isNaN(d.getTime())) return time;
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function appointmentStatusLabel(row: ObAppointmentRow): string {
  if (row.is_payment_done) return 'Payment';
  if (row.is_booked) return 'Booked';
  if (row.is_sms_verified || row.is_call_verified) return 'SMS Verified';
  return row.verification_status || 'Pending';
}

export function appointmentStatusTone(
  label: string,
): 'success' | 'info' | 'warning' | 'neutral' | 'gold' {
  const key = label.toLowerCase();
  if (key.includes('verified') || key.includes('booked') || key === 'payment') return 'success';
  if (key.includes('pending')) return 'warning';
  if (key.includes('fail') || key.includes('cancel')) return 'warning';
  if (key.includes('paid')) return 'gold';
  return 'neutral';
}
